// @flow
import * as React from 'react';
import Box from 'sail/Box';
import {usePrefs, useLocation, useVideos} from 'docs/data';
import type {VideoModel, VideoPlaylistModel} from 'docs/data';
import {useVideoPlayer} from 'docs/hooks';
import type {SettingType} from 'docs/data/integration-builder';
import {Settings, ContentSkeleton} from 'docs/components';
import type {VideoPlaylist} from './data/types';
import {usePersistentWatchHistory} from './data/usePersistentWatchHistory';
import {MissingPrefsCallout} from './MissingPrefsCallout';
import {VideoInfo} from './VideoInfo';
import {PlaylistVideoSelector} from './PlaylistVideoSelector';
import {
  getTopicID,
  videoMatchesTopicFromURL,
  prefVideosForPlaylist,
} from './data/util';
import './PlaylistPlayer.scss';

const LoadingSkeleton = () => (
  <Box
    flex={{direction: 'column'}}
    flexGrow={1}
    style={{paddingLeft: '5vw', paddingRight: '5vw'}}
  >
    <Box
      background="surface"
      style={{
        height: '40px',
        width: '100%',
        marginTop: '32px',
        marginBottom: '32px',
        borderRadius: '4px',
      }}
    />
    <Box
      flex={{direction: 'row', justifyContent: 'space-between'}}
      flexGrow={1}
      background="surface"
      style={{width: '100%', height: '400px', marginBottom: '32px'}}
    />
    <Box flex={{direction: 'row'}} flexGrow={1} style={{width: '100%'}}>
      <ContentSkeleton />
    </Box>
  </Box>
);

type PlaylistPlayerInnerProps = {|
  playlist: VideoPlaylistModel,
  currentVideo: VideoModel,
  playlistTitle: string,
  settings: SettingType,
  videosList: VideoPlaylist,
  setCurrentTopic: (topicID: string) => void,
  setSecondsWatchedForVideo: (string, string, number) => void,
  secondsWatchedForVideo?: number,
|};
const PlaylistPlayerInner = ({
  playlist,
  currentVideo,
  settings,
  playlistTitle,
  videosList,
  setSecondsWatchedForVideo,
  secondsWatchedForVideo,
  setCurrentTopic,
}: PlaylistPlayerInnerProps) => {
  const {
    frame,
    isPlaying,
    isLoaded,
    playVideo,
    pauseVideo,
    requestCurrentTime,
    currentTime,
    loadNewVideo,
    hasCompletedPlayback,
  } = useVideoPlayer({
    video: currentVideo,
  });
  const onPlayClick = () => {
    if (isPlaying) {
      pauseVideo();
    } else {
      playVideo();
    }
  };

  // Load new videos when the current video id changes
  const secondsWatched = React.useRef(secondsWatchedForVideo);
  secondsWatched.current = secondsWatchedForVideo;
  React.useEffect(() => {
    if (isLoaded) {
      // If a user plays back a video they already watched,
      // restart it.
      let startTime: number = secondsWatched.current || 0;
      const duration = currentVideo.duration || 0;
      if (startTime + 3 >= duration) {
        startTime = 0;
      }
      loadNewVideo(currentVideo.youtubeId, startTime);
    }
  }, [currentVideo.youtubeId, currentVideo.duration, isLoaded, loadNewVideo]);

  // Poll YoutubeAPI for current time
  React.useEffect(() => {
    if (isPlaying) {
      // This polls the youtube API to get the current time, which updates
      // the current time state.
      const interval = setInterval(() => {
        requestCurrentTime();
      }, 1000);

      return () => {
        clearInterval(interval);
      };
    }
  }, [isPlaying, requestCurrentTime]);

  // Update seconds watched (which is persisted in storage)
  const setSecondsWatched = React.useRef(setSecondsWatchedForVideo);
  setSecondsWatched.current = setSecondsWatchedForVideo;
  React.useEffect(() => {
    if (isPlaying) {
      const videoID = currentVideo.id;
      const topicID = getTopicID(currentVideo);
      setSecondsWatched.current(videoID, topicID, currentTime);
    }
  }, [isPlaying, currentTime, currentVideo]);

  // Auto advance videos on completion
  const advancePlaylistFn = () => {
    const nextIndex = videosList.findIndex(({current}) => current) + 1;
    if (videosList[nextIndex]) {
      setCurrentTopic(getTopicID(videosList[nextIndex].video));
    }
  };
  const advancePlaylist = React.useRef(advancePlaylistFn);
  advancePlaylist.current = advancePlaylistFn;
  React.useEffect(() => {
    if (hasCompletedPlayback) {
      advancePlaylist.current();
    }
  }, [hasCompletedPlayback]);

  return (
    <>
      <Box padding={{bottom: 16}}>
        <Settings
          type={settings}
          onSettingChange={(prefs) => {
            setCurrentTopic(getTopicID(currentVideo));
          }}
        />
      </Box>
      <MissingPrefsCallout video={currentVideo} />
      <Box
        id={playlist.id}
        className="PlaylistPlayer"
        padding={{top: 16, bottom: 12}}
      >
        <Box
          className="PlaylistPlayer-row-container"
          flex={{justifyContent: 'space-between'}}
        >
          <Box className="PlaylistPlayer-left-container " margin={{right: 32}}>
            <Box
              className="PlaylistPlayer-video-container"
              onClick={onPlayClick}
            >
              {frame}
            </Box>
          </Box>
          <Box className="PlaylistPlayer-right-container">
            <PlaylistVideoSelector
              videosList={videosList}
              setCurrentTopic={setCurrentTopic}
              currentVideo={currentVideo}
              playlistTitle={playlistTitle}
              playlistID={playlist.id}
              isCurrentVideoPlaying={isPlaying}
              onPlayClick={onPlayClick}
            />
          </Box>
        </Box>
        <VideoInfo currentTime={currentTime} video={currentVideo} />
      </Box>
    </>
  );
};

type PlaylistPlayerProps = {|
  playlist: string,
  className?: string,
  children?: React.Node,
  settings: SettingType,
  title: string,
|};

export const PlaylistPlayer = ({
  playlist,
  title,
  settings,
  className = '',
}: PlaylistPlayerProps) => {
  const {getPref} = usePrefs();
  const {location, replaceLocation} = useLocation();

  // Fetch the playlist from the playlist data with the string ID given from markdoc.
  // Playlists are defined in: docs/videos/playlists.yaml
  const videos = useVideos({prefetchAllVideos: true});
  const fetchedPlaylist = videos.getPlaylist(playlist);

  // Load watch data from persistent storage.
  const {
    setCurrentTopic,
    setSecondsWatchedForVideo,
    currentTopicID,
    watchHistory,
    isLoadingPlaylistHistory,
  } = usePersistentWatchHistory(playlist);

  // Ensure the playlist is fetched and the watch history isn't being read (async)
  // from persistent storage (IndexedDB)
  if (!fetchedPlaylist || isLoadingPlaylistHistory) {
    // This essentially ensures the component is only rendered on the client
    return <LoadingSkeleton />;
  }

  const prefs = {
    lang: getPref('lang'),
    client: getPref('client'),
    platform: getPref('platform'),
  };
  const prefVideos = prefVideosForPlaylist(fetchedPlaylist, prefs);

  // The precedence order for how we pick which video in a playlist to show
  // - if a valid video is specified in the url parameter, pick that
  // - if a video is selected from storage, pick that
  // - otherwise, just show the first one

  // It is slightly confusing, because internally we have the idea of "topic"
  // which is the programming language-agnostic mapping for videos.
  const videoFromURLParam = prefVideos.find((video) =>
    videoMatchesTopicFromURL(video, location.query.video),
  );
  const videoFromStorage = prefVideos.find(
    (video) => getTopicID(video) === currentTopicID,
  );

  const currentVideo = videoFromURLParam || videoFromStorage || prefVideos[0];

  const videosList: VideoPlaylist = prefVideos.map((video) => {
    const topicID = getTopicID(video);
    const topic = watchHistory.find((t) => t.topicID === topicID);
    const current = video.id === currentVideo.id;
    const secondsWatched = topic?.videos[video.id]?.secondsWatched || 0;
    return {
      secondsWatched,
      current,
      video,
    };
  });

  if (!videosList.find((v) => v.current)) {
    videosList[0].current = true;
  }

  const secondsWatchedForVideo = videosList.find(
    ({video}) => video.id === currentVideo.id,
  )?.secondsWatched;

  return (
    <>
      <PlaylistPlayerInner
        playlist={fetchedPlaylist}
        currentVideo={currentVideo}
        playlistTitle={title}
        settings={settings}
        videosList={videosList}
        setSecondsWatchedForVideo={setSecondsWatchedForVideo}
        secondsWatchedForVideo={secondsWatchedForVideo}
        setCurrentTopic={(topic) => {
          replaceLocation({
            query: {},
          });
          setCurrentTopic(topic);
        }}
      />
    </>
  );
};
